import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Bell,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  CreditCard,
  IndianRupee,
  LayoutDashboard,
  Menu,
  MoreHorizontal,
  Search,
  ShieldCheck,
  UserRound,
  Users,
  WalletCards,
  X,
  XCircle,
  ExternalLink,
  RefreshCw,
  Loader2,
  AlertCircle,
  Plus,
} from "lucide-react";

import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../firebase/firebase";

const navItems = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "users", label: "Users", icon: Users },
  { id: "tasks", label: "Tasks", icon: ClipboardList },
  { id: "taskSubmissions", label: "Task Submissions", icon: CheckCircle2 },
  { id: "referrals", label: "Referrals", icon: BarChart3 },
  { id: "withdrawals", label: "Withdrawals", icon: WalletCards },
];

function addOneMonth(date) {
  const next = new Date(date);
  const originalDay = next.getDate();
  next.setMonth(next.getMonth() + 1);

  // Handle months with fewer days (e.g. Jan 31 -> Feb end).
  if (next.getDate() < originalDay) {
    next.setDate(0);
  }

  return next;
}


function getTimestampValue(value) {
  if (!value) return 0;

  try {
    if (typeof value?.toMillis === "function") return value.toMillis();
    if (typeof value?.toDate === "function") return value.toDate().getTime();
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  } catch {
    return 0;
  }
}


function formatDate(value) {
  if (!value) return "—";

  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : value instanceof Date
          ? value
          : new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "—";
  }
}

function getUserName(user) {
  return user?.name || user?.displayName || user?.email?.split("@")[0] || "User";
}

export default function AdminDashboard() {
  const [activeSection, setActiveSection] = useState("overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [firebaseUser, setFirebaseUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);

  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskSubmissions, setTaskSubmissions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  const [actionLoading, setActionLoading] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!firebaseUser) {
      setPayments([]);
      setUsers([]);
      setTasks([]);
      setTaskSubmissions([]);
      setReferrals([]);
      setWithdrawals([]);
      setDataLoading(false);
      return undefined;
    }

    setDataLoading(true);

    const unsubscribers = [];

    unsubscribers.push(
      onSnapshot(
        collection(db, "payments"),
        (snapshot) => {
          const rows = snapshot.docs
            .map((item) => ({
              id: item.id,
              ...item.data(),
            }))
            .sort((a, b) => {
              const aTime = a.submittedAt?.toMillis?.() || 0;
              const bTime = b.submittedAt?.toMillis?.() || 0;
              return bTime - aTime;
            });

          setPayments(rows);
          setDataLoading(false);
        },
        (error) => {
          console.error("Payments listener error:", error);
          setActionError(error.message || "Unable to load payments.");
          setDataLoading(false);
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "users"),
        (snapshot) => {
          setUsers(
            snapshot.docs.map((item) => ({
              id: item.id,
              ...item.data(),
            }))
          );
        },
        (error) => {
          console.error("Users listener error:", error);
          setActionError(error.message || "Unable to load users.");
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "tasks"),
        (snapshot) => {
          setTasks(
            snapshot.docs.map((item) => ({
              id: item.id,
              ...item.data(),
            }))
          );
        },
        (error) => {
          console.error("Tasks listener error:", error);
          setActionError(error.message || "Unable to load tasks.");
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "taskSubmissions"),
        (snapshot) => {
          const rows = snapshot.docs
            .map((item) => ({
              id: item.id,
              ...item.data(),
            }))
            .sort((a, b) => {
              const aTime = a.submittedAt?.toMillis?.() || 0;
              const bTime = b.submittedAt?.toMillis?.() || 0;
              return bTime - aTime;
            });

          setTaskSubmissions(rows);
        },
        (error) => {
          console.error("Task submissions listener error:", error);
          setActionError(
            error.message || "Unable to load task submissions."
          );
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "referrals"),
        (snapshot) => {
          setReferrals(
            snapshot.docs.map((item) => ({
              id: item.id,
              ...item.data(),
            }))
          );
        },
        (error) => {
          console.error("Referrals listener error:", error);
          setActionError(error.message || "Unable to load referrals.");
        }
      )
    );

    unsubscribers.push(
      onSnapshot(
        collection(db, "withdrawals"),
        (snapshot) => {
          const rows = snapshot.docs
            .map((item) => ({
              id: item.id,
              ...item.data(),
            }))
            .sort((a, b) => {
              const aTime = a.createdAt?.toMillis?.() || 0;
              const bTime = b.createdAt?.toMillis?.() || 0;
              return bTime - aTime;
            });

          setWithdrawals(rows);
        },
        (error) => {
          console.error("Withdrawals listener error:", error);
          setActionError(error.message || "Unable to load withdrawals.");
        }
      )
    );

    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [firebaseUser]);

  const userMap = useMemo(() => {
    return users.reduce((map, user) => {
      map[user.id] = user;
      return map;
    }, {});
  }, [users]);

  const paymentRows = useMemo(() => {
    return payments.map((payment) => {
      const user = userMap[payment.uid] || {};
      return {
        ...payment,
        name: getUserName(user),
        email: user.email || payment.email || "—",
        phone: user.phone || payment.phone || "—",
        date: formatDate(payment.submittedAt),
        screenshot: payment.screenshotUrl || "",
      };
    });
  }, [payments, userMap]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return users;

    return users.filter((user) =>
      [
        user.name,
        user.displayName,
        user.email,
        user.phone,
        user.referralCode,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [search, users]);

  const pendingPayments = paymentRows.filter(
    (payment) => payment.status === "pending"
  ).length;

  const approvedPayments = paymentRows.filter(
    (payment) => payment.status === "approved"
  ).length;

  const pendingWithdrawals = withdrawals.filter(
    (item) => item.status === "pending" || item.status === "processing"
  ).length;

  const activeUsers = users.filter(
    (user) => user.softwareActivated === true
  ).length;

  const clearActionState = () => {
    setActionMessage("");
    setActionError("");
  };

  const deleteRecord = async (collectionName, id, label = "record") => {
    if (!id) return;

    // Admin profiles are protected and can never be deleted from this panel.
    if (collectionName === "users") {
      const targetUser = users.find((user) => user.id === id);

      if (targetUser?.role === "admin") {
        setActionError("Admin accounts cannot be deleted.");
        return;
      }
    }

    const confirmed = window.confirm(
      `Delete this ${label}? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      clearActionState();
      setDeleteLoading(`${collectionName}-${id}`);
      await deleteDoc(doc(db, collectionName, id));
      setActionMessage(`${label} deleted successfully.`);
    } catch (error) {
      console.error(`Delete ${label} error:`, error);
      setActionError(
        error?.message || `Unable to delete this ${label}.`
      );
    } finally {
      setDeleteLoading("");
    }
  };

  const updatePayment = async (payment, status) => {
    if (!firebaseUser || !payment?.id || !payment?.uid) return;

    const actionKey = `${status}-${payment.id}`;

    try {
      clearActionState();
      setActionLoading(actionKey);

      const paymentRef = doc(db, "payments", payment.id);
      const userRef = doc(db, "users", payment.uid);
      const batch = writeBatch(db);

      batch.update(paymentRef, {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: firebaseUser.uid,
      });

      const existingBuyer = userMap[payment.uid] || {};
      const existingExpiryMs = getTimestampValue(existingBuyer.subscriptionExpiryDate);
      const existingSubscriptionActive =
        Boolean(existingBuyer.softwareActivated) &&
        (!existingExpiryMs || existingExpiryMs > Date.now());

      const subscriptionStart = new Date();
      const subscriptionExpiry = addOneMonth(subscriptionStart);

      batch.update(userRef,
        status === "approved"
          ? {
              paymentStatus: status,
              softwareActivated: true,
              subscriptionStatus: "active",
              subscriptionStartDate: subscriptionStart,
              subscriptionExpiryDate: subscriptionExpiry,
              updatedAt: serverTimestamp(),
            }
          : {
              paymentStatus: status,
              // Do not revoke an already-active subscription merely because
              // a renewal payment was rejected.
              softwareActivated: existingSubscriptionActive,
              subscriptionStatus: existingSubscriptionActive ? "active" : "inactive",
              updatedAt: serverTimestamp(),
            }
      );

      if (status === "approved") {
        const buyer = userMap[payment.uid] || {};
        const referralCode = String(buyer.referredBy || "").trim();

        if (referralCode) {
          const referrer = users.find(
            (user) =>
              String(user.referralCode || "").trim().toLowerCase() ===
              referralCode.toLowerCase()
          );

          if (referrer && referrer.id !== payment.uid) {
            const existingReferral = referrals.find(
              (item) =>
                item.paymentId === payment.id ||
                (item.referredUid === payment.uid &&
                  item.referrerUid === referrer.id)
            );

            if (!existingReferral) {
              const referrerWalletRef = doc(db, "wallets", referrer.id);
              const referrerWalletSnapshot = await getDoc(referrerWalletRef);
              const existingWallet = referrerWalletSnapshot.exists()
                ? referrerWalletSnapshot.data()
                : {};

              const currentAvailable = Number(existingWallet.availableBalance || 0);
              const currentPending = Number(existingWallet.pendingBalance || 0);
              const currentEarned = Number(existingWallet.totalEarned || 0);
              const currentWithdrawn = Number(existingWallet.totalWithdrawn || 0);
              const commission = 200;

              batch.set(
                referrerWalletRef,
                {
                  availableBalance: currentAvailable + commission,
                  pendingBalance: currentPending,
                  totalEarned: currentEarned + commission,
                  totalWithdrawn: currentWithdrawn,
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              );

              const referralRef = doc(collection(db, "referrals"));
              batch.set(referralRef, {
                referrerUid: referrer.id,
                referredUid: payment.uid,
                referrerName: getUserName(referrer),
                referredName: getUserName(buyer),
                referredEmail: buyer.email || "",
                paymentId: payment.id,
                commission,
                status: "approved",
                createdAt: serverTimestamp(),
                approvedAt: serverTimestamp(),
                approvedBy: firebaseUser.uid,
              });

              const transactionRef = doc(collection(db, "transactions"));
              batch.set(transactionRef, {
                uid: referrer.id,
                type: "referral_commission",
                source: "referral_payment",
                referredUid: payment.uid,
                paymentId: payment.id,
                amount: commission,
                status: "completed",
                createdAt: serverTimestamp(),
                createdBy: firebaseUser.uid,
              });
            }
          }
        }
      }

      await batch.commit();

      setActionMessage(
        status === "approved"
          ? "Payment approved and user software access activated."
          : "Payment rejected and user software access remains locked."
      );
    } catch (error) {
      console.error("Payment update error:", error);
      setActionError(
        error?.message || "Unable to update this payment. Please try again."
      );
    } finally {
      setActionLoading("");
    }
  };

  const updateWithdrawal = async (withdrawal, status) => {
    if (!firebaseUser || !withdrawal?.id || !withdrawal?.uid) return;

    const actionKey = `withdrawal-${status}-${withdrawal.id}`;

    try {
      clearActionState();
      setActionLoading(actionKey);

      const withdrawalRef = doc(db, "withdrawals", withdrawal.id);
      const walletRef = doc(db, "wallets", withdrawal.uid);
      const walletSnapshot = await getDoc(walletRef);
      const existingWallet = walletSnapshot.exists() ? walletSnapshot.data() : {};
      const amount = Math.max(0, Number(withdrawal.amount || 0));

      const batch = writeBatch(db);

      batch.update(withdrawalRef, {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: firebaseUser.uid,
      });

      if (status === "paid") {
        const currentAvailable = Number(existingWallet.availableBalance || 0);
        const currentEarned = Number(existingWallet.totalEarned || 0);
        const currentPending = Number(existingWallet.pendingBalance || 0);
        const currentWithdrawn = Number(existingWallet.totalWithdrawn || 0);

        if (amount > currentAvailable) {
          throw new Error("This withdrawal exceeds the user's current wallet balance.");
        }

        batch.set(
          walletRef,
          {
            availableBalance: currentAvailable - amount,
            pendingBalance: currentPending,
            totalEarned: currentEarned,
            totalWithdrawn: currentWithdrawn + amount,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        const transactionRef = doc(collection(db, "transactions"));
        batch.set(transactionRef, {
          uid: withdrawal.uid,
          type: "withdrawal",
          source: "withdrawal_payout",
          withdrawalId: withdrawal.id,
          amount: -amount,
          status: "completed",
          createdAt: serverTimestamp(),
          createdBy: firebaseUser.uid,
        });
      }

      await batch.commit();

      setActionMessage(
        status === "paid"
          ? `Withdrawal marked as paid and ${amount ? `₹${amount}` : "the payout"} deducted from the wallet.`
          : "Withdrawal request rejected."
      );
    } catch (error) {
      console.error("Withdrawal update error:", error);
      setActionError(
        error?.message || "Unable to update withdrawal. Please try again."
      );
    } finally {
      setActionLoading("");
    }
  };

  const updateTaskSubmission = async (submission, status) => {
    if (!firebaseUser || !submission?.id || !submission?.uid) return;

    const actionKey = `submission-${status}-${submission.id}`;

    try {
      clearActionState();
      setActionLoading(actionKey);

      const submissionRef = doc(db, "taskSubmissions", submission.id);
      const userWalletRef = doc(db, "wallets", submission.uid);
      const walletSnapshot = await getDoc(userWalletRef);

      const task = tasks.find((item) => item.id === submission.taskId);
      const reward = Math.max(0, Number(task?.reward || submission.reward || 0));

      const batch = writeBatch(db);

      batch.update(submissionRef, {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: firebaseUser.uid,
      });

      if (status === "approved") {
        const existingWallet = walletSnapshot.exists()
          ? walletSnapshot.data()
          : {};

        const currentAvailable = Number(
          existingWallet.availableBalance || 0
        );
        const currentPending = Number(
          existingWallet.pendingBalance || 0
        );
        const currentEarned = Number(
          existingWallet.totalEarned || 0
        );
        const currentWithdrawn = Number(
          existingWallet.totalWithdrawn || 0
        );

        batch.set(
          userWalletRef,
          {
            availableBalance: currentAvailable + reward,
            pendingBalance: currentPending,
            totalEarned: currentEarned + reward,
            totalWithdrawn: currentWithdrawn,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        const transactionRef = doc(
          collection(db, "transactions")
        );

        batch.set(transactionRef, {
          uid: submission.uid,
          type: "task_reward",
          source: "task_submission",
          taskId: submission.taskId || "",
          taskSubmissionId: submission.id,
          amount: reward,
          status: "completed",
          createdAt: serverTimestamp(),
          createdBy: firebaseUser.uid,
        });
      }

      await batch.commit();

      setActionMessage(
        status === "approved"
          ? reward > 0
            ? `Task approved. ₹${reward} added to the user's wallet.`
            : "Task approved. No reward amount was configured."
          : "Task submission rejected."
      );
    } catch (error) {
      console.error("Task submission update error:", error);
      setActionError(
        error?.message ||
          "Unable to update this task submission. Please try again."
      );
    } finally {
      setActionLoading("");
    }
  };

  const createTask = async (taskData) => {
    if (!firebaseUser) return;

    try {
      clearActionState();
      setActionLoading("create-task");

      await addDoc(collection(db, "tasks"), {
        title: taskData.title.trim(),
        description: taskData.description.trim(),
        reward: Number(taskData.reward),
        assignedTo: taskData.assignedTo.trim(),
        url: taskData.url.trim(),
        status: "active",
        createdBy: firebaseUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setActionMessage("Task created successfully.");
    } catch (error) {
      console.error("Task creation error:", error);
      setActionError(error?.message || "Unable to create task.");
    } finally {
      setActionLoading("");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/login";
    } catch (error) {
      console.error("Admin logout error:", error);
    }
  };

  const pageTitle =
    navItems.find((item) => item.id === activeSection)?.label || "Overview";

  if (authLoading) {
    return <LoadingScreen text="Checking admin session..." />;
  }

  if (!firebaseUser) {
    return <LoadingScreen text="Redirecting to login..." />;
  }

  return (
    <div className="min-h-screen bg-[#f6f8fb] text-[#08111f]">
      <div className="flex min-h-screen">
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-[270px] border-r border-slate-200 bg-white transition-transform duration-300 lg:static lg:translate-x-0 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-full flex-col">
            <div className="flex h-[76px] items-center justify-between border-b border-slate-100 px-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00a96d] text-lg font-black text-white shadow-lg shadow-emerald-100">
                  R
                </div>
                <div>
                  <div className="text-lg font-black tracking-[-0.04em]">
                    Refrex
                  </div>
                  <div className="text-[9px] font-black uppercase tracking-[0.16em] text-slate-400">
                    Admin Panel
                  </div>
                </div>
              </div>

              <button
                onClick={() => setMobileOpen(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 lg:hidden"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-4 pt-6">
              <div className="mb-3 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                Management
              </div>

              <div className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = activeSection === item.id;
                  const pendingTaskSubmissions = taskSubmissions.filter(
                    (item) => item.status === "pending"
                  ).length;

                  const badge =
                    item.id === "payments"
                      ? pendingPayments
                      : item.id === "taskSubmissions"
                        ? pendingTaskSubmissions
                        : item.id === "withdrawals"
                          ? pendingWithdrawals
                          : 0;

                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveSection(item.id);
                        setMobileOpen(false);
                        clearActionState();
                      }}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-bold transition-all ${
                        active
                          ? "bg-emerald-50 text-[#00a96d]"
                          : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      <Icon size={18} />
                      <span className="flex-1">{item.label}</span>

                      {badge > 0 && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-100 px-1.5 text-[10px] font-black text-rose-600">
                          {badge}
                        </span>
                      )}

                      {active && <ChevronRight size={15} />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-auto border-t border-slate-100 p-4">
              <div className="rounded-2xl bg-[#07121f] p-4 text-white">
                <div className="flex items-center gap-2 text-emerald-300">
                  <ShieldCheck size={16} />
                  <span className="text-xs font-bold">Admin Access</span>
                </div>
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  Manage approvals, users, tasks and withdrawals from one
                  place.
                </p>
              </div>

              <div className="mt-4 flex items-center gap-3 rounded-xl px-2 py-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-[#00a96d]">
                  <UserRound size={17} />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-xs font-black">
                    {getUserName(firebaseUser)}
                  </div>
                  <div className="truncate text-[10px] text-slate-400">
                    {firebaseUser.email}
                  </div>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-500 hover:bg-slate-50"
              >
                Logout
              </button>
            </div>
          </div>
        </aside>

        {mobileOpen && (
          <button
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-slate-950/40 lg:hidden"
          />
        )}

        <main className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
            <div className="flex h-[76px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setMobileOpen(true)}
                  className="rounded-xl border border-slate-200 p-2.5 text-slate-600 lg:hidden"
                >
                  <Menu size={20} />
                </button>

                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
                    Refrex Admin
                  </div>
                  <h1 className="mt-0.5 text-lg font-black tracking-[-0.03em] sm:text-xl">
                    {pageTitle}
                  </h1>
                </div>
              </div>

              <div className="hidden items-center gap-3 sm:flex">
                <div className="relative">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search users..."
                    className="h-10 w-52 rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-xs font-medium outline-none transition focus:border-emerald-300 focus:bg-white"
                  />
                </div>

                <button className="relative rounded-xl border border-slate-200 p-2.5 text-slate-500 hover:bg-slate-50">
                  <Bell size={17} />
                  {pendingPayments + pendingWithdrawals > 0 && (
                    <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-rose-500" />
                  )}
                </button>

                <button
                  onClick={() => window.location.reload()}
                  className="rounded-xl border border-slate-200 p-2.5 text-slate-500 hover:bg-slate-50"
                  title="Refresh"
                >
                  <RefreshCw size={17} />
                </button>
              </div>
            </div>
          </header>

          <div className="mx-auto max-w-[1500px] p-4 sm:p-6 lg:p-8">
            {actionMessage && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                <span>{actionMessage}</span>
                <button
                  onClick={() => setActionMessage("")}
                  className="ml-auto"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {actionError && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <span className="break-words">{actionError}</span>
                <button
                  onClick={() => setActionError("")}
                  className="ml-auto"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {dataLoading && (
              <div className="mb-5 flex items-center gap-2 rounded-xl bg-white px-4 py-3 text-xs font-bold text-slate-500 shadow-sm">
                <Loader2 size={15} className="animate-spin" />
                Loading live Firebase data...
              </div>
            )}

            {activeSection === "overview" && (
              <Overview
                pendingPayments={pendingPayments}
                approvedPayments={approvedPayments}
                totalUsers={users.length}
                activeUsers={activeUsers}
                pendingWithdrawals={pendingWithdrawals}
                onPayments={() => setActiveSection("payments")}
                onWithdrawals={() => setActiveSection("withdrawals")}
                payments={paymentRows.slice(0, 5)}
              />
            )}

            {activeSection === "payments" && (
              <Payments
                payments={paymentRows}
                onUpdate={updatePayment}
                actionLoading={actionLoading}
                onDelete={(payment) => deleteRecord("payments", payment.id, "payment")}
                deleteLoading={deleteLoading}
              />
            )}

            {activeSection === "users" && (
              <UsersSection users={filteredUsers} onDelete={(user) => deleteRecord("users", user.id, "user profile")} deleteLoading={deleteLoading} />
            )}

            {activeSection === "tasks" && (
              <TasksSection
                tasks={tasks}
                onCreate={createTask}
                actionLoading={actionLoading}
                onDelete={(task) => deleteRecord("tasks", task.id, "task")}
                deleteLoading={deleteLoading}
              />
            )}

            {activeSection === "taskSubmissions" && (
              <TaskSubmissionsSection
                submissions={taskSubmissions}
                tasks={tasks}
                users={users}
                onUpdate={updateTaskSubmission}
                actionLoading={actionLoading}
                onDelete={(submission) => deleteRecord("taskSubmissions", submission.id, "task submission")}
                deleteLoading={deleteLoading}
              />
            )}

            {activeSection === "referrals" && (
              <ReferralsSection
                referrals={referrals}
                users={users}
                onDelete={(referral) => deleteRecord("referrals", referral.id, "referral")}
                deleteLoading={deleteLoading}
              />
            )}

            {activeSection === "withdrawals" && (
              <WithdrawalsSection
                withdrawals={withdrawals}
                users={users}
                onUpdate={updateWithdrawal}
                actionLoading={actionLoading}
                onDelete={(item) => deleteRecord("withdrawals", item.id, "withdrawal")}
                deleteLoading={deleteLoading}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function Overview({
  pendingPayments,
  approvedPayments,
  totalUsers,
  activeUsers,
  pendingWithdrawals,
  onPayments,
  onWithdrawals,
  payments,
}) {
  const stats = [
    ["Total Users", totalUsers, "Live Firestore data", Users],
    ["Pending Payments", pendingPayments, "Needs review", CreditCard],
    ["Activated Users", activeUsers, "Software access", CheckCircle2],
    ["Approved Payments", approvedPayments, "Verified", IndianRupee],
  ];

  return (
    <div>
      <div className="mb-7">
        <div className="text-xs font-bold text-slate-400">
          Good morning, Admin
        </div>
        <h2 className="mt-1 text-2xl font-black tracking-[-0.05em] sm:text-3xl">
          Platform Overview
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
          Monitor users, verify software payments, manage tasks and process
          wallet withdrawals.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(([label, value, change, Icon]) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-[#00a96d]">
              <Icon size={20} />
            </div>
            <div className="mt-5 text-2xl font-black tracking-[-0.04em]">
              {value}
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-slate-400">
                {label}
              </span>
              <span className="text-[10px] font-bold text-emerald-600">
                {change}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.45fr_1fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-black">Recent Payment Activity</h3>
              <p className="mt-1 text-xs text-slate-400">
                Latest software purchase submissions
              </p>
            </div>
            <button
              onClick={onPayments}
              className="text-xs font-black text-[#00a96d] hover:underline"
            >
              View all
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {payments.length === 0 ? (
              <EmptyState text="No payment submissions yet." />
            ) : (
              payments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex flex-col gap-3 rounded-xl border border-slate-100 p-3 sm:flex-row sm:items-center"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                    <CreditCard size={17} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-black">
                      {payment.name}
                    </div>
                    <div className="mt-0.5 text-[10px] text-slate-400">
                      {payment.id.slice(0, 12)} · {payment.date}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <span className="text-sm font-black">
                      ₹{payment.amount || 0}
                    </span>
                    <StatusBadge status={payment.status || "pending"} />
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-black">Withdrawal Queue</h3>
              <p className="mt-1 text-xs text-slate-400">
                Requests waiting for processing
              </p>
            </div>
            <button
              onClick={onWithdrawals}
              className="rounded-xl bg-slate-50 p-2 text-slate-500"
            >
              <WalletCards size={16} />
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {pendingWithdrawals === 0 ? (
              <EmptyState text="No pending withdrawals." />
            ) : (
              <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                <div className="text-2xl font-black">
                  {pendingWithdrawals}
                </div>
                <div className="mt-1 text-xs font-semibold text-amber-700">
                  withdrawal request(s) need admin action
                </div>
                <button
                  onClick={onWithdrawals}
                  className="mt-4 inline-flex items-center rounded-xl bg-white px-3 py-2 text-xs font-black text-amber-700 shadow-sm"
                >
                  Review withdrawals
                  <ChevronRight size={14} className="ml-1" />
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="mt-6 rounded-2xl bg-[#07121f] p-6 text-white sm:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-emerald-300">
              <ShieldCheck size={17} />
              <span className="text-xs font-black uppercase tracking-[0.14em]">
                Approval workflow
              </span>
            </div>
            <h3 className="mt-2 text-xl font-black tracking-[-0.03em]">
              Review payments before unlocking user access.
            </h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
              Payment screenshots are stored as compressed data URLs inside
              Firestore. Approving a payment activates the user's software
              access.
            </p>
          </div>

          <button
            onClick={onPayments}
            className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[#00a96d] px-5 text-sm font-black text-white hover:bg-[#00945f]"
          >
            Review Payments
            <ChevronRight size={17} className="ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Payments({ payments, onUpdate, actionLoading, onDelete, deleteLoading }) {
  return (
    <div>
      <SectionHeader
        eyebrow="Payment Verification"
        title="Software Payments"
        description="Manually review ₹1,000 monthly subscription payments and update their approval status."
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <MiniStat
          label="Pending"
          value={payments.filter((p) => p.status === "pending").length}
        />
        <MiniStat
          label="Approved"
          value={payments.filter((p) => p.status === "approved").length}
        />
        <MiniStat
          label="Rejected"
          value={payments.filter((p) => p.status === "rejected").length}
        />
      </div>

      {payments.length === 0 ? (
        <EmptyState text="No payment submissions found." />
      ) : (
        <div className="space-y-4">
          {payments.map((payment) => {
            const approveLoading =
              actionLoading === `approved-${payment.id}`;
            const rejectLoading =
              actionLoading === `rejected-${payment.id}`;

            return (
              <div
                key={payment.id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="grid gap-5 p-5 lg:grid-cols-[220px_1fr_auto] lg:items-center">
                  <div className="relative min-h-[180px] overflow-hidden rounded-xl bg-slate-100">
                    {payment.screenshot ? (
                      <img
                        src={payment.screenshot}
                        alt={`Payment screenshot from ${payment.name}`}
                        className="h-full min-h-[180px] w-full object-cover"
                      />
                    ) : (
                      <div className="flex min-h-[180px] items-center justify-center px-4 text-center text-xs font-bold text-slate-400">
                        No payment screenshot available
                      </div>
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-black">
                        {payment.name}
                      </h3>
                      <StatusBadge status={payment.status || "pending"} />
                    </div>

                    <p className="mt-1 break-all text-xs text-slate-400">
                      {payment.email}
                    </p>

                    <p className="mt-1 text-xs text-slate-400">
                      Phone: {payment.phone}
                    </p>

                    <div className="mt-5 grid gap-4 sm:grid-cols-3">
                      <InfoItem
                        label="Payment ID"
                        value={payment.id.slice(0, 16)}
                      />
                      <InfoItem
                        label="Amount"
                        value={`₹${payment.amount || 0}`}
                      />
                      <InfoItem
                        label="Submitted"
                        value={payment.date}
                      />
                    </div>

                    {payment.reviewedAt && (
                      <div className="mt-4 text-[10px] font-semibold text-slate-400">
                        Reviewed: {formatDate(payment.reviewedAt)}
                      </div>
                    )}

                    {payment.screenshot && (
                      <a
                        href={payment.screenshot}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 inline-flex items-center text-xs font-black text-[#00a96d] hover:underline"
                      >
                        Open Full Screenshot
                        <ExternalLink size={14} className="ml-1.5" />
                      </a>
                    )}
                  </div>

                  <div className="flex flex-row gap-2 lg:flex-col">
                    <button
                      disabled={
                        payment.status === "approved" ||
                        approveLoading ||
                        rejectLoading
                      }
                      onClick={() => onUpdate(payment, "approved")}
                      className="inline-flex min-h-10 items-center justify-center rounded-xl bg-[#00a96d] px-4 text-xs font-black text-white hover:bg-[#00945f] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {approveLoading ? (
                        <Loader2 size={15} className="mr-1.5 animate-spin" />
                      ) : (
                        <Check size={15} className="mr-1.5" />
                      )}
                      Approve
                    </button>

                    <button
                      disabled={
                        payment.status === "rejected" ||
                        approveLoading ||
                        rejectLoading
                      }
                      onClick={() => onUpdate(payment, "rejected")}
                      className="inline-flex min-h-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 px-4 text-xs font-black text-rose-600 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {rejectLoading ? (
                        <Loader2 size={15} className="mr-1.5 animate-spin" />
                      ) : (
                        <XCircle size={15} className="mr-1.5" />
                      )}
                      Reject
                    </button>
                    <button
                      disabled={deleteLoading === `payments-${payment.id}`}
                      onClick={() => onDelete(payment)}
                      className="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-xs font-black text-slate-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                    >
                      {deleteLoading === `payments-${payment.id}` ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <X size={14} className="mr-1.5" />}
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UsersSection({ users, onDelete, deleteLoading }) {
  return (
    <div>
      <SectionHeader
        eyebrow="User Management"
        title="All Users"
        description="View account, referral and payment verification status from live Firestore data."
      />

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead className="border-b border-slate-100 bg-slate-50">
              <tr>
                {[
                  "User",
                  "Phone",
                  "Referral Code",
                  "Payment",
                  "Software",
                  "Joined",
                  "Action",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-5 py-4 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-12 text-center text-xs font-semibold text-slate-400"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const name = getUserName(user);
                  const joined = formatDate(user.createdAt);

                  return (
                    <tr
                      key={user.id}
                      className={`border-b border-slate-100 last:border-0 ${
                        user.role === "admin" ? "bg-emerald-50/20" : ""
                      }`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-xs font-black text-[#00a96d]">
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-black">{name}</div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                              <div className="break-all text-[10px] text-slate-400">
                                {user.email || "—"}
                              </div>

                              {user.role === "admin" && (
                                <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[8px] font-black uppercase tracking-wide text-emerald-700">
                                  Admin
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                        {user.phone || "—"}
                      </td>

                      <td className="px-5 py-4 text-xs font-bold text-slate-600">
                        {user.referralCode || "—"}
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge
                          status={user.paymentStatus || "not_paid"}
                        />
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge
                          status={
                            user.softwareActivated
                              ? "active"
                              : "locked"
                          }
                        />
                      </td>

                      <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                        {joined}
                      </td>

                      <td className="px-5 py-4">
                        <button
                          className="rounded-lg p-2 text-slate-400 hover:bg-slate-50"
                          title="User details"
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {user.role === "admin" ? (
                          <span
                            className="ml-1 inline-flex items-center rounded-lg bg-slate-50 px-2 py-2 text-[8px] font-black uppercase tracking-wide text-slate-400"
                            title="Admin account is protected"
                          >
                            Protected
                          </span>
                        ) : (
                          <button
                            disabled={deleteLoading === `users-${user.id}`}
                            onClick={() => onDelete(user)}
                            className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                            title="Delete user profile"
                          >
                            {deleteLoading === `users-${user.id}` ? (
                              <Loader2 size={17} className="animate-spin" />
                            ) : (
                              <X size={17} />
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TasksSection({ tasks, onCreate, actionLoading, onDelete, deleteLoading }) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [reward, setReward] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [url, setUrl] = useState("");

  const submit = async (event) => {
    event.preventDefault();

    if (!title.trim()) return;
    if (!reward || Number(reward) < 0) return;

    if (url.trim()) {
      try {
        const parsedUrl = new URL(url.trim());
        if (!["http:", "https:"].includes(parsedUrl.protocol)) {
          return;
        }
      } catch {
        return;
      }
    }

    await onCreate({
      title,
      description,
      reward,
      assignedTo,
      url,
    });

    setTitle("");
    setDescription("");
    setReward("");
    setAssignedTo("");
    setUrl("");
    setShowForm(false);
  };

  return (
    <div>
      <SectionHeader
        eyebrow="Task Management"
        title="Assigned Tasks"
        description="Create and manage campaign tasks stored in Firestore."
        action={
          <button
            onClick={() => setShowForm((value) => !value)}
            className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[#00a96d] px-4 text-xs font-black text-white hover:bg-[#00945f]"
          >
            <Plus size={15} className="mr-1.5" />
            {showForm ? "Close" : "Create Task"}
          </button>
        }
      />

      {showForm && (
        <form
          onSubmit={submit}
          className="mb-5 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5"
        >
          <div className="grid gap-3 md:grid-cols-2">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-emerald-300"
              placeholder="Task title"
              required
            />

            <input
              value={reward}
              onChange={(e) => setReward(e.target.value)}
              type="number"
              min="0"
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-emerald-300"
              placeholder="Reward ₹"
              required
            />

            <input
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-emerald-300"
              placeholder="Assigned user UID (optional)"
            />

            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-emerald-300"
              placeholder="Short task description"
            />

            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              type="url"
              className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-xs outline-none focus:border-emerald-300 md:col-span-2"
              placeholder="Task URL (https://...) — optional"
            />

            <button
              type="submit"
              disabled={actionLoading === "create-task"}
              className="h-11 rounded-xl bg-[#07121f] text-xs font-black text-white disabled:opacity-50 md:col-span-2"
            >
              {actionLoading === "create-task" ? (
                <span className="inline-flex items-center">
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Creating...
                </span>
              ) : (
                "Create Task"
              )}
            </button>
          </div>
        </form>
      )}

      {tasks.length === 0 ? (
        <EmptyState text="No tasks found." />
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-[#00a96d]">
                  <ClipboardList size={18} />
                </div>
                <StatusBadge status={task.status || "active"} />
              </div>

              <h3 className="mt-5 font-black">{task.title || "Untitled Task"}</h3>

              {task.description && (
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  {task.description}
                </p>
              )}

              <div className="mt-4 flex justify-between text-xs">
                <span className="font-semibold text-slate-400">Reward</span>
                <span className="font-black">₹{task.reward || 0}</span>
              </div>

              <div className="mt-2 flex justify-between text-xs">
                <span className="font-semibold text-slate-400">Assigned</span>
                <span className="max-w-[160px] truncate font-black">
                  {task.assignedTo || "All users"}
                </span>
              </div>

              {task.url && (
                <a
                  href={task.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center text-xs font-black text-[#00a96d] hover:underline"
                >
                  Open Task URL
                  <ExternalLink size={13} className="ml-1.5" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TaskSubmissionsSection({
  submissions,
  tasks,
  users,
  onUpdate,
  actionLoading,
  onDelete,
  deleteLoading,
}) {
  const userMap = users.reduce((map, user) => {
    map[user.id] = user;
    return map;
  }, {});

  const taskMap = tasks.reduce((map, task) => {
    map[task.id] = task;
    return map;
  }, {});

  const pending = submissions.filter(
    (item) => item.status === "pending"
  ).length;

  const approved = submissions.filter(
    (item) => item.status === "approved" || item.status === "completed"
  ).length;

  const rejected = submissions.filter(
    (item) => item.status === "rejected"
  ).length;

  return (
    <div>
      <SectionHeader
        eyebrow="Task Verification"
        title="Task Submissions"
        description="Review completed user tasks and approve or reject their submissions. Approved rewards are added to the user's wallet."
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <MiniStat label="Pending" value={pending} />
        <MiniStat label="Approved" value={approved} />
        <MiniStat label="Rejected" value={rejected} />
      </div>

      {submissions.length === 0 ? (
        <EmptyState text="No task submissions found yet." />
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => {
            const user = userMap[submission.uid] || {};
            const task = taskMap[submission.taskId] || {};

            const approveLoading =
              actionLoading ===
              `submission-approved-${submission.id}`;

            const rejectLoading =
              actionLoading ===
              `submission-rejected-${submission.id}`;

            const reward = Number(
              task.reward ?? submission.reward ?? 0
            );

            return (
              <div
                key={submission.id}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-[#00a96d]">
                    <ClipboardList size={19} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-black">
                        {getUserName(user)}
                      </h3>
                      <StatusBadge
                        status={submission.status || "pending"}
                      />
                    </div>

                    <div className="mt-1 break-all text-xs text-slate-400">
                      {user.email || submission.uid}
                    </div>

                    <div className="mt-4 grid gap-4 sm:grid-cols-4">
                      <InfoItem
                        label="Task"
                        value={task.title || submission.taskId || "—"}
                      />
                      <InfoItem
                        label="Reward"
                        value={`₹${reward}`}
                      />
                      <InfoItem
                        label="Submitted"
                        value={formatDate(submission.submittedAt)}
                      />
                      <InfoItem
                        label="Submission ID"
                        value={submission.id.slice(0, 12)}
                      />
                    </div>

                    {submission.reviewedAt && (
                      <div className="mt-3 text-[10px] font-semibold text-slate-400">
                        Reviewed: {formatDate(submission.reviewedAt)}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      disabled={
                        submission.status === "approved" ||
                        submission.status === "completed" ||
                        approveLoading ||
                        rejectLoading
                      }
                      onClick={() =>
                        onUpdate(submission, "approved")
                      }
                      className="inline-flex min-h-10 items-center justify-center rounded-xl bg-[#00a96d] px-4 text-xs font-black text-white hover:bg-[#00945f] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {approveLoading ? (
                        <Loader2
                          size={14}
                          className="mr-1.5 animate-spin"
                        />
                      ) : (
                        <Check size={14} className="mr-1.5" />
                      )}
                      Approve
                    </button>

                    <button
                      disabled={
                        submission.status === "rejected" ||
                        approveLoading ||
                        rejectLoading
                      }
                      onClick={() =>
                        onUpdate(submission, "rejected")
                      }
                      className="inline-flex min-h-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 px-4 text-xs font-black text-rose-600 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {rejectLoading ? (
                        <Loader2
                          size={14}
                          className="mr-1.5 animate-spin"
                        />
                      ) : (
                        <XCircle size={14} className="mr-1.5" />
                      )}
                      Reject
                    </button>
                    <button
                      disabled={deleteLoading === `taskSubmissions-${submission.id}`}
                      onClick={() => onDelete(submission)}
                      className="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-xs font-black text-slate-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                    >
                      {deleteLoading === `taskSubmissions-${submission.id}` ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <X size={14} className="mr-1.5" />}
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReferralsSection({ referrals, users, onDelete, deleteLoading }) {
  const userMap = users.reduce((map, user) => {
    map[user.id] = user;
    return map;
  }, {});

  const verified = referrals.filter(
    (item) =>
      item.status === "approved" ||
      item.status === "verified" ||
      item.verified === true
  ).length;

  const commission = referrals.reduce((total, item) => {
    const value =
      Number(item.commission ?? item.reward ?? item.amount ?? 0) || 0;
    return total + value;
  }, 0);

  return (
    <div>
      <SectionHeader
        eyebrow="Referral Management"
        title="Referral Activity"
        description="Track referral records and the ₹200 commission workflow."
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <MiniStat label="Total Referrals" value={referrals.length} />
        <MiniStat label="Verified Purchases" value={verified} />
        <MiniStat
          label="Commission Recorded"
          value={`₹${commission}`}
        />
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        {referrals.length === 0 ? (
          <EmptyState text="No referral records found yet." />
        ) : (
          <div className="space-y-3">
            {referrals.map((item) => {
              const referredUser = userMap[item.referredUid] || {};
              const referrerUser = userMap[item.referrerUid] || {};

              return (
                <div
                  key={item.id}
                  className="flex flex-col gap-3 rounded-xl border border-slate-100 p-4 sm:flex-row sm:items-center"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-[#00a96d]">
                    <BarChart3 size={16} />
                  </div>

                  <div className="flex-1">
                    <div className="text-sm font-black">
                      {getUserName(referredUser)}
                    </div>

                    <div className="mt-1 text-[10px] font-bold text-slate-400">
                      Referred by {getUserName(referrerUser)}
                    </div>
                  </div>

                  <div className="text-sm font-black">
                    ₹{item.commission ?? item.reward ?? 0}
                  </div>

                  <StatusBadge status={item.status || "pending"} />
                  <button
                    disabled={deleteLoading === `referrals-${item.id}`}
                    onClick={() => onDelete(item)}
                    className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                    title="Delete referral record"
                  >
                    {deleteLoading === `referrals-${item.id}` ? <Loader2 size={15} className="animate-spin" /> : <X size={15} />}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function WithdrawalsSection({ withdrawals = [], users = [], onUpdate, actionLoading, onDelete, deleteLoading }) {
  return (
    <div>
      <SectionHeader
        eyebrow="Wallet Management"
        title="Withdrawal Requests"
        description="Review UPI/QR withdrawal requests and mark completed transactions."
      />

      {(withdrawals || []).length === 0 ? (
        <EmptyState text="No withdrawal requests found." />
      ) : (
        <div className="space-y-4">
          {(withdrawals || []).map((item) => {
            const payLoading =
              actionLoading === `withdrawal-paid-${item.id}`;
            const rejectLoading =
              actionLoading === `withdrawal-rejected-${item.id}`;

            const upi =
              item.paymentMethod?.upiId ||
              item.upi ||
              item.paymentMethod?.value ||
              "—";

            return (
              <div
                key={item.id}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex flex-col gap-5 lg:flex-row lg:items-center">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-[#00a96d]">
                    <WalletCards size={19} />
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-black">
                        {getUserName(
                          (users || []).find((user) => user.id === item.uid)
                        )}
                      </h3>
                      <StatusBadge status={item.status || "pending"} />
                    </div>

                    <div className="mt-1 text-xs text-slate-400">
                      {item.id} · {formatDate(item.createdAt || item.submittedAt)}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">
                      UPI
                    </div>
                    <div className="mt-1 text-sm font-bold">{upi}</div>
                  </div>

                  <div>
                    <div className="text-[10px] font-black uppercase tracking-wide text-slate-400">
                      Amount
                    </div>
                    <div className="mt-1 text-lg font-black">
                      ₹{item.amount || 0}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      disabled={
                        item.status === "paid" ||
                        payLoading ||
                        rejectLoading
                      }
                      onClick={() => onUpdate(item, "paid")}
                      className="inline-flex min-h-10 items-center rounded-xl bg-[#00a96d] px-4 text-xs font-black text-white hover:bg-[#00945f] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {payLoading ? (
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                      ) : (
                        <Check size={14} className="mr-1.5" />
                      )}
                      Mark Paid
                    </button>

                    <button
                      disabled={
                        item.status === "rejected" ||
                        payLoading ||
                        rejectLoading
                      }
                      onClick={() => onUpdate(item, "rejected")}
                      className="inline-flex min-h-10 items-center rounded-xl border border-rose-200 bg-rose-50 px-4 text-xs font-black text-rose-600 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {rejectLoading ? (
                        <Loader2 size={14} className="mr-1.5 animate-spin" />
                      ) : (
                        <XCircle size={14} className="mr-1.5" />
                      )}
                      Reject
                    </button>
                    <button
                      disabled={deleteLoading === `withdrawals-${item.id}`}
                      onClick={() => onDelete(item)}
                      className="inline-flex min-h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-xs font-black text-slate-500 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                    >
                      {deleteLoading === `withdrawals-${item.id}` ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <X size={14} className="mr-1.5" />}
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ eyebrow, title, description, action }) {
  return (
    <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600">
          {eyebrow}
        </div>
        <h2 className="mt-1 text-2xl font-black tracking-[-0.05em] sm:text-3xl">
          {title}
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
          {description}
        </p>
      </div>
      {action}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-2xl font-black tracking-[-0.04em]">{value}</div>
      <div className="mt-1 text-xs font-bold text-slate-400">{label}</div>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-black uppercase tracking-[0.12em] text-slate-400">
        {label}
      </div>
      <div className="mt-1 break-words text-xs font-black text-slate-700">
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const normalized = String(status || "unknown").toLowerCase();

  const styles = {
    approved: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    rejected: "bg-rose-50 text-rose-700",
    paid: "bg-emerald-50 text-emerald-700",
    processing: "bg-blue-50 text-blue-700",
    active: "bg-emerald-50 text-emerald-700",
    draft: "bg-slate-100 text-slate-600",
    locked: "bg-slate-100 text-slate-600",
    not_paid: "bg-slate-100 text-slate-600",
    verified: "bg-emerald-50 text-emerald-700",
    unknown: "bg-slate-100 text-slate-600",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-wide ${
        styles[normalized] || styles.unknown
      }`}
    >
      {normalized.replaceAll("_", " ")}
    </span>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-slate-50 text-slate-400">
        <ClipboardList size={18} />
      </div>
      <p className="mt-3 text-xs font-semibold text-slate-400">{text}</p>
    </div>
  );
}

function LoadingScreen({ text }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f6f8fb]">
      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-bold text-slate-600 shadow-sm">
        <Loader2 size={18} className="animate-spin text-[#00a96d]" />
        {text}
      </div>
    </div>
  );
}
